// Module dependencies
import context from 'app/common/lib/context';
import experiments from 'app/common/lib/experiments';
import pubSub from 'app/common/lib/PubSub';
import { logAutoClientRefreshDesktopEvent } from 'app/packages/auto-client-refresh';
import { logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';
import { unregisterServiceWorker, updateServiceWorker } from 'app/common/lib/swUtil';

/**
 * @enum {number}
 */
const AppVersionState = {
  CURRENT: 1,
  NEW_VERSION_REQUIRED: 3,
};

/**
 * @constructor
 */
const AppVersion = function() {
  /** @type {string} */
  this.currentVersion = '';
  this.versionState = AppVersionState.CURRENT;
  this.preparingNewVersion = false;
};

Object.assign(AppVersion.prototype, {
  /**
   * Setup the AppVersion instance with the current version.
   *
   * @param {string} version The current version.
   */
  setup(version) {
    this.currentVersion = version;
    this.nextVersion = null;

    // Subscribe to the site:appVersion topic and
    // call our update version method when messaged.
    pubSub.instance.subscribe(
      PCONST.PUBSUB_CHANNEL_SITE,
      PCONST.PUBSUB_TOPIC_APP_VERSION,
      (channel, topic, { version, originHost }) => {
        this._checkVersion(version, originHost);
      },
    );
  },

  /**
   * Compares the version passed in to the version used to load the initial
   * page and JS bundle. If the versions are different this marks the current
   * state as NEW_VERSION_REQUIRED.
   *
   * @param {string} version The new version.
   * @private
   */
  _checkVersion(version, originHost) {
    if (version !== this.currentVersion) {
      logCustomTraceEvent({ name: 'NewAppVersionDetected' });
      if (this.versionState !== AppVersionState.NEW_VERSION_REQUIRED) {
        logAutoClientRefreshDesktopEvent(
          'signal',
          'new_version_required',
          context,
          version,
          originHost,
        );
        // if the server says there's a version mismatch, reload on next navigate
        this.versionState = AppVersionState.NEW_VERSION_REQUIRED;
      }
      const updateSWExpGroup =
        experiments.getInstance().v2ActivateExperiment('web_perf_update_sw') || '';
      if (
        updateSWExpGroup.startsWith('enabled') &&
        version !== this.nextVersion &&
        !this.preparingNewVersion
      ) {
        this.preparingNewVersion = true;
        // attempt to update the service worker
        // if there is an error, attempt to unregister the service worker completely
        updateServiceWorker()
          .catch(() => unregisterServiceWorker('checkVersion'))
          .finally(() => {
            this.preparingNewVersion = false;
          });
        this.nextVersion = version;
      }
    }
  },

  /**
   * Checks whether a new version of the app is required
   * @return {boolean}
   */
  isNewVersionRequired() {
    return this.versionState === AppVersionState.NEW_VERSION_REQUIRED;
  },
});

export default new AppVersion();
